<template>
  <van-dialog
    v-model="localShow"
    :title="dialogTitle"
    theme="round-button"
    confirm-button-color="#aa341b"
    :before-close="beforeClose"
    @confirm="onConfirm"
  >
    <div class="diglog-close" @click="closeDialog">×</div>

    <div v-if="loading" class="loading">
      <van-loading size="24px" type="spinner" color="#aa341b" />
      <span>加载中...</span>
    </div>

    <div v-else class="diglog-questions">
      <div v-if="physicianName" class="physician-name">
        医生：{{ physicianName }}
      </div>

      <!-- 仅渲染可见题 -->
      <div v-for="item in visibleItems" :key="item.idx" class="question">
        <p class="question-text">
          {{ item.idx + 1 }}. {{ item.q.text }}
          <span v-if="item.q.required !== false" class="req">*</span>
        </p>

        <!-- 自由文本题 -->
        <van-field
          v-if="isTextQuestion(item.q) && answers[item.idx]"
          v-model="answers[item.idx].value"
          type="textarea"
          :rows="item.q.rows || 2"
          :autosize="item.q.autosize !== false"
          :maxlength="item.q.maxlength || 200"
          show-word-limit
          :placeholder="item.q.placeholder || '请输入'"
          class="q-textarea"
        />

        <!-- 单选题（默认） -->
        <van-radio-group
          v-else-if="answers[item.idx]"
          v-model="answers[item.idx].value"
          :direction="item.q.direction || 'vertical'"
        >
          <van-radio v-for="opt in item.q.options" :key="opt.value" :name="opt.value">
            {{ opt.labelPrefix || '' }}{{ opt.label }}
          </van-radio>
        </van-radio-group>

        <p v-if="showTips && isMissing(item.idx)" class="error">这是必答题，请填写/选择答案！</p>
      </div>
    </div>
  </van-dialog>
</template>

<script>
import { RadioGroup, Radio, Loading, Field } from 'vant';
import { getLocalStorage } from '@/utils';
import { getQuestionnaireById, saveQuestionnaireResult } from '@/api/content';

export default {
  name: 'DynamicQuestionnaireDialog',
  components: {
    'van-radio-group': RadioGroup,
    'van-radio': Radio,
    'van-loading': Loading,
    'van-field': Field,
  },
  props: {
    show: { type: Boolean, default: false },
    questionnaireId: { type: String, required: true },
    physicianId: { type: String, default: '' },
    physicianName: { type: String, default: '' },
    accountId: { type: String, default: '' },
    recorderType: { type: String, default: 'employee' },
    recorderId: { type: String, default: '' },
    recorderName: { type: String, default: '' },
    // 若后端仅支持 content_id 查询，可传入 fallback；否则会用 questionnaireId 兜底
    contentIdFallback: { type: String, default: '' },
  },
  data() {
    return {
      localShow: this.show,
      loading: false,
      dialogTitle: '问卷',
      qid: '',
      questions: [],
      answers: [],
      showTips: false,
      submitting: false,
    };
  },
  computed: {
    // 计算当前可见题目（携带原始索引）
    visibleItems() {
      return this.questions
        .map((q, idx) => ({ q, idx }))
        .filter(({ q, idx }) => this.isQuestionVisible(q, idx));
    },
  },
  watch: {
    show(val) {
      this.localShow = val;
      if (val) this.bootstrap();
    },
    localShow(val) {
      if (!val) this.$emit('update:show', false);
    },
    answers: {
      deep: true,
      handler() {
        this.clearHiddenAnswers();
      },
    },
  },
  mounted() {
    if (this.localShow) this.bootstrap();
  },
  methods: {
    async bootstrap() {
      this.showTips = false;
      await this.loadQuestionnaire();
    },
    async loadQuestionnaire() {
      this.loading = true;

      // 占位题 + 立刻初始化 answers，避免首次渲染时报 undefined
      this.questions = [
        {
          text: '此次拜访中是否谈及最新版指南对伏硫西汀的一线推荐？',
          required: true,
          options: [
            { value: '1_yes', label: '是', labelPrefix: 'A. ' },
            { value: '2_no', label: '否', labelPrefix: 'B. ' },
          ],
        },
        {
          text: '客户会选择什么类型的患者来尝试伏硫西汀一线使用？',
          type: 'textarea',
          required: true,
          rows: 3,
          maxlength: 1000,
          placeholder: '请输入（必填）',
          showIf: { question: 1, equals: '1_yes' },
        },
        {
          text: '本次拜访后，客户提及哪些异议？你的后续计划如何？',
          type: 'textarea',
          required: true,
          rows: 3,
          maxlength: 1000,
          placeholder: '请输入（必填）',
          showIf: { question: 1, equals: '1_yes' },
        },
      ];
      this.initAnswers(); // 关键：先初始化

      this.dialogTitle = '问卷';
      this.qid = '';

      try {
        const res = await getQuestionnaireById(this.questionnaireId);
        const env = res && res.data;
        const q = env?.data || env || null;
        if (!q) throw new Error('未获取到问卷');

        this.qid = q.questionnaire_id || this.questionnaireId;
        this.dialogTitle = q.questionnaire_name || q.title || '问卷';

        const parsed = this.tryParseQuestions(q.questions);
        if (!Array.isArray(parsed) || !parsed.length) throw new Error('问卷题目为空');

        this.questions = parsed;
        this.initAnswers(); // 用真实题目重建 answers
      } catch (e) {
        // 接口失败保留占位题，但确保 answers 已就绪，不再崩溃
        this.$toast && this.$toast.fail('问卷加载失败');
        // 如需自动关闭，可解开下一行
        // setTimeout(() => this.closeDialog(), 500);
      } finally {
        this.loading = false;
      }
    },
    initAnswers() {
      this.answers = (this.questions || []).map((q, idx) => ({
        index: idx + 1,
        question: q.text,
        value: '',
        label: '',
      }));
    },
    // 识别自由文本题
    isTextQuestion(q) {
      const t = String(q?.type || q?.input || q?.kind || '').toLowerCase();
      return ['text', 'textarea', 'input', 'free', 'freetext'].includes(t) || q?.widget === 'textarea';
    },
    // 判断题目是否可见（支持 showIf / visibleWhen）
    isQuestionVisible(q, idx) {
      const cond = q && (q.visibleWhen || q.showIf);
      if (!cond) return true;

      const evalOne = (c) => {
        if (!c) return true;
        // 定位被依赖题目
        const ref = c.questionIndex !== undefined
          ? c.questionIndex
          : (c.question ?? c.index ?? c.q ?? c.ref);
        let refIdx = -1;
        if (c.questionIndex !== undefined) {
          refIdx = Number(ref);
        } else if (typeof ref === 'number') {
          refIdx = ref - 1; // 题号从1开始
        } else if (typeof ref === 'string') {
          const m = ref.match(/^q(\d+)$/i);
          refIdx = m ? parseInt(m[1], 10) - 1 : parseInt(ref, 10) - 1;
        }
        if (!(refIdx >= 0 && refIdx < this.answers.length)) return false;

        const val = this.answers[refIdx]?.value;
        const refQ = this.questions[refIdx] || {};
        const label = (refQ.options || []).find(o => o.value === val)?.label;

        // 值匹配
        if ('value' in c) return Array.isArray(c.value) ? c.value.includes(val) : val === c.value;
        if ('values' in c) return Array.isArray(c.values) ? c.values.includes(val) : val === c.values;
        if ('equals' in c) return Array.isArray(c.equals) ? c.equals.includes(val) : val === c.equals;
        if ('in' in c) return Array.isArray(c.in) ? c.in.includes(val) : false;

        // 标签匹配（备用）
        if ('labelEquals' in c) return Array.isArray(c.labelEquals) ? c.labelEquals.includes(label) : label === c.labelEquals;
        if ('labelIn' in c) return Array.isArray(c.labelIn) ? c.labelIn.includes(label) : false;

        return !!val; // 默认：有作答即显示
      };

      if (Array.isArray(cond)) {
        // 默认 AND
        return cond.every(evalOne);
      }
      if (cond.allOf && Array.isArray(cond.allOf)) {
        return cond.allOf.every(evalOne);
      }
      if (cond.anyOf && Array.isArray(cond.anyOf)) {
        return cond.anyOf.some(evalOne);
      }
      return evalOne(cond);
    },
    clearHiddenAnswers() {
      (this.questions || []).forEach((q, idx) => {
        if (this.isQuestionVisible(q, idx) || !this.answers[idx] || this.answers[idx].value === '') return;
        this.$set(this.answers, idx, {
          ...this.answers[idx],
          value: '',
          label: '',
        });
      });
    },

    // 是否缺失（仅对“可见且必填”的题校验）
    isMissing(idx) {
      const q = this.questions[idx] || {};
      if (!this.isQuestionVisible(q, idx)) return false;
      if (q.required === false) return false;
      const v = (this.answers[idx]?.value ?? '');
      return this.isTextQuestion(q) ? String(v).trim() === '' : !v;
    },
    beforeClose(action, done) {
      if (action === 'confirm') {
        const allFilled = this.visibleItems.every(({ idx }) => !this.isMissing(idx));
        if (!allFilled) {
          this.showTips = true;
          done(false);
          return;
        }
      }
      done();
    },
    async onConfirm() {
      const allFilled = this.visibleItems.every(({ idx }) => !this.isMissing(idx));
      if (!allFilled) {
        this.showTips = true;
        return;
      }

      // 仅提交可见题答案
      const visibleSet = new Set(this.visibleItems.map(i => i.idx));
      const completed = this.answers
        .map((ans, idx) => {
          if (!visibleSet.has(idx)) return null;
          const q = this.questions[idx] || {};
          if (this.isTextQuestion(q)) {
            return { ...ans, label: String(ans.value) };
          }
          const opt = (q.options || []).find(o => o.value === ans.value);
          return { ...ans, label: opt ? opt.label : '' };
        })
        .filter(Boolean);

      const payload = {
        questionnaire_id: this.qid || this.questionnaireId,
        account_id: String(this.accountId || getLocalStorage('account_id') || ''),
        open_id: '',
        physician_id: String(this.physicianId || ''),
        answers: JSON.stringify(completed),
        recorder_type: this.recorderType || 'employee',
        recorder_id: String(this.recorderId || getLocalStorage('user_id') || ''),
        recorder_name: String(this.recorderName || getLocalStorage('user_name') || getLocalStorage('user_id') || ''),
      };

      try {
        this.submitting = true;
        await saveQuestionnaireResult(payload);
        this.$toast.success('提交成功');
        this.$emit('finish', { questionnaireId: this.qid, answers: completed });
        this.closeDialog();
      } catch (e) {
        this.$toast.fail('提交失败，请稍后重试');
      } finally {
        this.submitting = false;
      }
    },
    closeDialog() {
      this.localShow = false;
      this.$emit('close');
    },
    tryParseQuestions(raw) {
      if (!raw) return null;

      // 已是数组
      if (Array.isArray(raw)) return raw;

      // 对象包裹：{ questions: [...] }
      if (typeof raw === 'object') {
        if (Array.isArray(raw.questions)) return raw.questions;
        return null;
      }

      // 字符串：尝试 JSON.parse
      if (typeof raw === 'string') {
        try {
          const j = JSON.parse(raw);
          if (Array.isArray(j)) return j;
          if (j && Array.isArray(j.questions)) return j.questions;
        } catch (e) {
          console.error('JSON parse error:', e);
        }
        // 宽松解析（兼容后端返回 JS 风格字符串）
        try {
          // eslint-disable-next-line no-new-func
          const v = new Function(`return (${raw})`)();
          if (Array.isArray(v)) return v;
          if (v && Array.isArray(v.questions)) return v.questions;
        } catch (e) {
          console.error('Function parse error:', e);
        }
      }

      return null;
    },
  },
};
</script>

<style scoped>
.diglog-close {
  position: absolute;
  font-size: 30px;
  top: 25px;
  right: 20px;
  cursor: pointer;
}
.loading {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 200px;
  justify-content: center;
  color: #666;
}
.physician-name {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: left;
}
.diglog-questions {
  max-height: 480px;
  overflow-y: auto;
  padding: 20px 20px 0 20px;
  text-align: left;
}
.question {
  margin-bottom: 20px;
}
.question-text {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
  line-height: 1.5;
}
.req { color: #cc4b47; }
.error {
  font-size: 14px;
  color: #aa341b;
  margin-top: 5px;
}
::v-deep .van-radio {
  font-size: 14px;
  align-items: flex-start;
  margin-bottom: 5px;
}
::v-deep .van-radio__icon--checked .van-icon,
::v-deep .van-checkbox__icon--checked .van-icon {
  background-color: #aa341b;
  border-color: #aa341b;
}
.q-textarea {
  border: 1px solid #e8e2da;
  border-radius: 5px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.92);
  box-sizing: border-box;
}
.q-textarea :deep(textarea) {
  min-height: 64px;
  line-height: 1.5;
}
</style>
